import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/footer";
import WhatsAppButton from "../components/WhatsAppButton";
import SeoHead from "../components/SeoHead";
import OptimizedImage from "../components/OptimizedImage";
import TableOfContents from "../components/TableOfContents";
import RelatedPosts from "../components/RelatedPosts";
import siteData from "../data/siteData";
import blogPosts from "../data/blogData";
import blogFallbackImage from "../assets/img/ai-automationhero.png";
import { createBlogPostingSchema, createBreadcrumbList, createFaqPageSchema } from "../utils/seo";
import { getBlogImage } from "../utils/blogImages";
import "./BlogPost.css";

const normalizeHeadingId = (value, index) =>
  `heading-${index}-${value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const estimateReadingTime = (contentHtml) => {
  const words = contentHtml.replace(/<[^>]*>/g, " ").split(/\s+/).filter(Boolean).length;
  return `${Math.max(3, Math.ceil(words / 200))} min read`;
};

const BlogPost = () => {
  const { slug } = useParams();
  const [activeSection, setActiveSection] = useState("");

  const post = useMemo(() => blogPosts.find((item) => item.slug === slug), [slug]);

  const contentAnalysis = useMemo(() => {
    if (!post?.contentHtml) {
      return { processedContent: "", tableOfContents: [], wordCount: 0 };
    }

    if (typeof window === "undefined" || typeof DOMParser === "undefined") {
      const wordCount = post.contentHtml.replace(/<[^>]*>/g, " ").split(/\s+/).filter(Boolean).length;
      return { processedContent: post.contentHtml, tableOfContents: [], wordCount };
    }

    const parser = new DOMParser();
    const document = parser.parseFromString(post.contentHtml, "text/html");
    const headings = Array.from(document.querySelectorAll("h2"));

    const tableOfContents = headings.map((heading, index) => {
      const text = heading.textContent?.trim() || "Section";
      const id = normalizeHeadingId(text, index);
      heading.id = id;
      return { id, text };
    });

    const wordCount = document.body.textContent?.split(/\s+/).filter(Boolean).length || 0;

    return {
      processedContent: document.body.innerHTML,
      tableOfContents,
      wordCount,
    };
  }, [post]);

  const relatedPosts = useMemo(() => {
    if (!post) return [];

    return (post.relatedSlugs || [])
      .map((relatedSlug) => blogPosts.find((item) => item.slug === relatedSlug))
      .filter(Boolean)
      .map((item) => ({ ...item, image: getBlogImage(item, blogFallbackImage) }))
      .slice(0, 3);
  }, [post]);

  const canonicalPath = post ? `/blog/${post.slug}` : "/blog";
  const imageUrl = getBlogImage(post, blogFallbackImage);

  useEffect(() => {
    if (!contentAnalysis.tableOfContents.length) return;

    const handleScroll = () => {
      const headerOffset = 140;
      const scrollPosition = window.scrollY + headerOffset;
      let currentId = contentAnalysis.tableOfContents[0]?.id || "";

      for (const item of contentAnalysis.tableOfContents) {
        const element = document.getElementById(item.id);
        if (!element) continue;

        const offsetTop = element.getBoundingClientRect().top + window.scrollY;
        if (offsetTop <= scrollPosition) {
          currentId = item.id;
        } else {
          break;
        }
      }

      setActiveSection(currentId);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, [contentAnalysis.tableOfContents]);

  const handleTOCClick = (id) => {
    const element = document.getElementById(id);
    if (!element) return;

    const headerOffset = 120;
    const top = element.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    setActiveSection(id);
    window.scrollTo({ top, behavior: "smooth" });
  };

  if (!post) {
    return (
      <div className="min-h-screen bg-background text-slate-900">
        <SeoHead
          title="Blog Post Not Found | QBITS9"
          description="The blog post you are looking for could not be found. Explore the QBITS9 blog for AI automation and digital solutions insights."
          canonicalPath="/blog"
          noindex
        />
        <Header />
        <main className="min-h-screen flex items-center justify-center px-6">
          <div className="text-center max-w-xl">
            <h1 className="text-4xl font-unbounded font-bold text-black mb-4">Blog Post Not Found</h1>
            <p className="text-grey font-poppins mb-8">
              The article you are looking for does not exist or has been moved.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
            >
              Back to Blog
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const jsonLd = [
    createBlogPostingSchema({
      post,
      path: canonicalPath,
      imageUrl,
    }),
    createBreadcrumbList([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: post.title, path: canonicalPath },
    ]),
  ];

  if (post.faqs?.length) {
    jsonLd.push(
      createFaqPageSchema({
        faqs: post.faqs,
        path: canonicalPath,
        name: post.title,
        description: post.metaDescription || post.excerpt,
      }),
    );
  }

  return (
    <div className="min-h-screen bg-background text-slate-900">
      <SeoHead
        title={post.metaTitle || `${post.title} | QBITS9`}
        description={post.metaDescription || post.excerpt}
        canonicalPath={canonicalPath}
        image={imageUrl}
        imageAlt={post.title}
        type="article"
        jsonLd={jsonLd}
        author={post.author || siteData.siteName}
        datePublished={post.datePublished}
        dateModified={post.dateModified || post.datePublished}
        articleSection={post.category}
        keywords={post.tags || []}
      />

      <Header />

      <main className="min-h-screen bg-background">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-16">
          <div className="flex gap-8 lg:gap-12 relative items-start">
            <TableOfContents items={contentAnalysis.tableOfContents} activeId={activeSection} onSelect={handleTOCClick} />

            <article className="flex-1 min-w-0 w-full max-w-4xl">
              <header className="mb-8 relative">
                <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-grey font-poppins">
                  <Link to="/" className="hover:text-primary transition-colors">
                    Home
                  </Link>
                  <span>/</span>
                  <Link to="/blog" className="hover:text-primary transition-colors">
                    Blog
                  </Link>
                  <span>/</span>
                  <span className="text-slate-900">{post.title}</span>
                </nav>

                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                  <span className="inline-block px-3 py-1.5 text-xs sm:text-sm font-poppins font-medium bg-primary/10 text-primary rounded-full">
                    {post.category}
                  </span>
                  <span className="text-grey">•</span>
                  <time dateTime={post.datePublished} className="text-xs sm:text-sm text-grey font-poppins">
                    {formatDate(post.datePublished)}
                  </time>
                  <span className="text-grey">•</span>
                  <span className="text-xs sm:text-sm text-grey font-poppins">{estimateReadingTime(post.contentHtml)}</span>
                  <span className="text-grey">•</span>
                  <span className="text-xs sm:text-sm text-grey font-poppins">{post.author || siteData.siteName}</span>
                </div>

                <h1 className="text-3xl sm:text-4xl md:text-5xl font-unbounded font-bold text-black mb-6 leading-tight break-words">
                  {post.title}
                </h1>

                <div className="relative bg-gradient-to-br from-primary/5 via-white to-secondary/5 rounded-2xl p-5 sm:p-6 md:p-8 border-l-4 border-primary shadow-lg overflow-hidden">
                  <div className="absolute top-4 right-4 opacity-10 pointer-events-none">
                    <svg className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 text-primary" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                  </div>
                  <p className="relative text-base sm:text-lg md:text-xl text-lightBlack font-poppins leading-relaxed font-medium">
                    {post.excerpt}
                  </p>
                </div>
              </header>

              <div className="mb-8 sm:mb-12">
                <div className="relative w-full rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl aspect-video bg-gray-100">
                  <OptimizedImage
                    alt={post.title}
                    loading="eager"
                    priority
                    width="1920"
                    height="1080"
                    className="h-full w-full object-cover transform hover:scale-105 transition-transform duration-700"
                    src={imageUrl}
                  />
                </div>
              </div>

              <section className="mb-12">
                <div
                  className="blog-content w-full overflow-hidden"
                  dangerouslySetInnerHTML={{ __html: contentAnalysis.processedContent }}
                />
              </section>

              <section className="mb-12">
                <div className="overflow-x-auto rounded-xl shadow-lg border border-grey/20">
                  <table className="min-w-full bg-white">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-unbounded font-semibold uppercase tracking-wider">
                          Feature
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-unbounded font-semibold uppercase tracking-wider">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-grey/10">
                      <tr className="hover:bg-background transition-colors">
                        <td className="px-6 py-4 font-poppins font-semibold text-black">Published Date</td>
                        <td className="px-6 py-4 font-poppins text-grey">{formatDate(post.datePublished)}</td>
                      </tr>
                      <tr className="hover:bg-background transition-colors">
                        <td className="px-6 py-4 font-poppins font-semibold text-black">Last Updated</td>
                        <td className="px-6 py-4 font-poppins text-grey">{formatDate(post.dateModified || post.datePublished)}</td>
                      </tr>
                      <tr className="hover:bg-background transition-colors">
                        <td className="px-6 py-4 font-poppins font-semibold text-black">Author</td>
                        <td className="px-6 py-4 font-poppins text-grey">{post.author || siteData.siteName}</td>
                      </tr>
                      <tr className="hover:bg-background transition-colors">
                        <td className="px-6 py-4 font-poppins font-semibold text-black">Reading Time</td>
                        <td className="px-6 py-4 font-poppins text-grey">{estimateReadingTime(post.contentHtml)}</td>
                      </tr>
                      <tr className="hover:bg-background transition-colors">
                        <td className="px-6 py-4 font-poppins font-semibold text-black">Categories</td>
                        <td className="px-6 py-4 font-poppins text-grey">{post.category}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {post.tags?.length > 0 ? (
                <section className="pt-8 border-t-2 border-grey/20">
                  <h3 className="text-lg font-unbounded font-semibold text-black mb-3">Tags:</h3>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-block px-4 py-2 text-sm font-poppins bg-grey/10 text-grey rounded-lg hover:bg-primary/10 hover:text-primary transition-colors"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </section>
              ) : null}

              <RelatedPosts posts={relatedPosts} imageFallback={blogFallbackImage} />
            </article>
          </div>
        </div>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default BlogPost;
